/*
 * Copyright (c) 2023. World Wide Generation Ltd
 */

import { runStages } from '@g17eco/calculation-engine';
import { CalculationValue, GridDashboardChartItem } from '../../types/insight-custom-dashboard';
import { UniversalTrackerPlain } from '../../types/universalTracker';
import { handleRouteError } from '../../logger';
import { UniversalTrackerModalServiceUtrv } from '@g17eco/types/universalTrackerValue';

type StageUtrv = Pick<UniversalTrackerModalServiceUtrv, 'value' | 'valueData' | 'disaggregation' | 'valueType'>;

export interface UtrStageData {
  utr: Pick<UniversalTrackerPlain, 'code' | 'valueType'>;
  utrvs: StageUtrv[];
}

type StageResult = Record<string, number | string | undefined>;

export interface SetupData extends Pick<GridDashboardChartItem, 'variables'> {
  utrsData: UtrStageData[];
  stages: CalculationValue['stages'];
  fallback?: string | number;
  /** Receives every stage's result, e.g. for tooltips that read them after the value resolves */
  acc?: StageResult;
}

export const processCalculation = async ({ stages, variables, utrsData, fallback = '', acc }: SetupData) => {
  if (!stages?.length) {
    return fallback;
  }

  // Earlier values' results, so an annotation's result stage can read them
  const results = acc && Object.fromEntries(Object.entries(acc).filter((entry): entry is [string, number | string] => entry[1] !== undefined));
  const outcome = await runStages({ stages, variables, utrsData, results });
  if (!outcome.ok) {
    if (outcome.reason === 'error') {
      handleRouteError(outcome.error, { stage: outcome.stage });
    }
    return fallback;
  }

  if (acc) {
    Object.assign(acc, outcome.results);
  }
  return outcome.value;
};
